/**
 * Straight-line hop between two schedule pins.
 * Minutes are a rough walk at 3 mph — not a routed commute and not Directions.
 */

export const EARTH_RADIUS_MILES = 3958.7613;
export const WALK_MPH = 3;

export interface LatLng {
  lat: number;
  lng: number;
}

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

function toDeg(rad: number): number {
  return (rad * 180) / Math.PI;
}

function finitePoint(p: LatLng | null | undefined): p is LatLng {
  return !!p && Number.isFinite(p.lat) && Number.isFinite(p.lng);
}

/** Great-circle distance in miles. */
export function straightLineMiles(a: LatLng, b: LatLng): number {
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const dLat = lat2 - lat1;
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.asin(Math.min(1, Math.sqrt(h)));
  return EARTH_RADIUS_MILES * c;
}

/** Rough walking time. 3 mph → minutes = miles × 20, rounded. */
export function walkMinutes(miles: number, mph: number = WALK_MPH): number {
  if (!Number.isFinite(miles) || miles <= 0 || !Number.isFinite(mph) || mph <= 0) {
    return 0;
  }
  return Math.round((miles / mph) * 60);
}

export function formatMiles(miles: number): string {
  const rounded = Math.round(miles * 10) / 10;
  return (Number.isFinite(rounded) ? rounded : 0).toFixed(1);
}

/** Walk line under 2 mi; farther is distance only (`21 mi from Cajón — not a walk`). */
export const WALK_HOP_MAX_MILES = 2;

function formatLongMiles(miles: number): string {
  const shown = Math.round(miles * 10) / 10;
  if (Math.abs(shown - Math.round(shown)) < 1e-9) return String(Math.round(shown));
  return shown.toFixed(1);
}

export function formatHopLine(
  miles: number,
  minutes: number,
  fromName: string,
): string {
  const shown = Math.round(miles * 10) / 10;
  if (shown > WALK_HOP_MAX_MILES) {
    return `${formatLongMiles(miles)} mi from ${fromName} — not a walk`;
  }
  return `~${minutes} min walk · ${formatMiles(miles)} mi from ${fromName}`;
}

export function formatHopBetween(
  from: (LatLng & { name: string }) | null | undefined,
  to: LatLng | null | undefined,
): string | null {
  if (!finitePoint(from) || !finitePoint(to)) return null;
  const name = from.name.trim();
  if (!name) return null;
  const miles = straightLineMiles(from, to);
  if (!Number.isFinite(miles)) return null;
  return formatHopLine(miles, walkMinutes(miles), name);
}

/**
 * Hop from the nearest earlier stop that actually has a pin.
 * The first item, or an item with no previous pin, has no line.
 * The origin is whichever stop comes before — never a hardcoded hotel.
 */
export function hopLineAt<T extends LatLng & { name: string }>(
  stops: ReadonlyArray<T | null | undefined>,
  index: number,
): string | null {
  const current = stops[index];
  if (!finitePoint(current)) return null;
  for (let i = index - 1; i >= 0; i--) {
    const prev = stops[i];
    if (!finitePoint(prev)) continue;
    if (prev.lat === current.lat && prev.lng === current.lng) return null;
    return formatHopBetween(prev, current);
  }
  return null;
}

export function initialBearingDegrees(from: LatLng, to: LatLng): number {
  const lat1 = toRad(from.lat);
  const lat2 = toRad(to.lat);
  const dLng = toRad(to.lng - from.lng);
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

export function destinationPoint(
  from: LatLng,
  bearingDeg: number,
  miles: number,
): LatLng {
  const delta = miles / EARTH_RADIUS_MILES;
  const theta = toRad(bearingDeg);
  const lat1 = toRad(from.lat);
  const lng1 = toRad(from.lng);
  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(delta) +
      Math.cos(lat1) * Math.sin(delta) * Math.cos(theta),
  );
  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(theta) * Math.sin(delta) * Math.cos(lat1),
      Math.cos(delta) - Math.sin(lat1) * Math.sin(lat2),
    );
  return { lat: toDeg(lat2), lng: toDeg(lng2) };
}

/**
 * One short cue just before the active pin, pointing at it.
 * Not under the previous marker, and not a route polyline.
 */
export function shortHopArrow(
  from: LatLng,
  to: LatLng,
): (LatLng & { bearing: number }) | null {
  if (!finitePoint(from) || !finitePoint(to)) return null;
  const miles = straightLineMiles(from, to);
  if (!Number.isFinite(miles) || miles < 0.08) return null;
  const bearing = initialBearingDegrees(from, to);
  const backOff = Math.min(0.28, Math.max(0.1, miles * 0.2));
  const travel = miles - backOff;
  if (travel < 0.04) return null;
  return { ...destinationPoint(from, bearing, travel), bearing };
}
